'use client';

import clsx from 'clsx';
import { useState, useCallback, useMemo, useId } from 'react';
import styles from './Input.module.scss';
import Icon from '@publishing/components/ui/Icon';
import { ArrowTriangle } from '@publishing/components/ui/SvgSprite';
import type { SvgIconInput, SvgIconTheme } from '@publishing/components/ui/Icon';
import type { FieldSize } from '../formgroup/FormGroup';
import FormMessage from '@publishing/components/forms/_FormMessage';
import { DEFAULT_VALIDATORS, VARIANT_VALIDATORS, eulReul, eunNeun } from './_validator';
import { VARIANT_PRESETS, isInputPreset, type InputPropType, type InputPreset, type InputType } from './_types';
export type { VariantPreset, InputPropType, InputPreset, InputType, InputVariant } from './_types';

// ── Number / Tel helpers ─────────────────────────
function parseDigitsInt(raw: string): number {
  const digits = raw.replace(/,/g, '').replace(/\D/g, '');
  if (!digits) return 0;
  const n = parseInt(digits, 10);
  return Number.isNaN(n) ? 0 : n;
}

function applyComma(v: string): string {
  const digits = v.replace(/,/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('ko-KR');
}

function applyTel(v: string): string {
  const digits = v.replace(/\D/g, '').slice(0, 11);
  if (!digits) return '';
  if (digits.startsWith('02')) {
    if (digits.length <= 5) return digits.replace(/(\d{2})(\d+)/, '$1-$2');
    if (digits.length <= 9) return digits.replace(/(\d{2})(\d{3})(\d+)/, '$1-$2-$3');
    return digits.replace(/(\d{2})(\d{4})(\d+)/, '$1-$2-$3');
  }
  if (digits.length <= 6) return digits.replace(/(\d{3})(\d+)/, '$1-$2');
  if (digits.length <= 10) return digits.replace(/(\d{3})(\d{3})(\d+)/, '$1-$2-$3');
  return digits.replace(/(\d{3})(\d{4})(\d+)/, '$1-$2-$3');
}

function maskDate(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
}

function maskYearMonth(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 6);
  if (digits.length <= 4) return digits;
  return `${digits.slice(0, 4)}-${digits.slice(4)}`;
}

/** 예상 이용 인원 — 선행 0 제거, 값이 0이면 1 */
function normalizeUserCountDigits(digits: string): string {
  if (!digits) return '';
  const stripped = digits.replace(/^0+/, '') || '0';
  return stripped === '0' ? '1' : stripped;
}

// ── Static maps ─────────────────────────────────
const iconSizeMap: Record<FieldSize, number> = { lg: 24, md: 20, sm: 16 };

const htmlTypeMap: Partial<Record<InputType, React.HTMLInputTypeAttribute>> = {
  text: 'text',
  email: 'email',
  number: 'text',
  tel: 'text',
  date: 'text',
  yearMonth: 'text',
};

const autoCompleteMap: Partial<Record<InputType, string>> = {
  email: 'email',
  tel: 'tel',
};

const inputModeMap: Partial<Record<InputType, React.HTMLAttributes<HTMLInputElement>['inputMode']>> = {
  number: 'numeric',
  tel: 'tel',
  date: 'numeric',
  yearMonth: 'numeric',
};

// ── Props ────────────────────────────────────────
export interface InputProps {
  /**
   * 프리셋(`company`, `phone`, …, `email`) 또는 동작 타입(`text`, `number`, `tel`, `date`).
   * `type="email"`은 항상 이메일 **프리셋**입니다. 순수 이메일 입력만 쓰려면 프리셋과 동일하게 쓰면 됩니다.
   */
  type?: InputPropType;
  /** DOM `<input type="…">` 강제 지정 (예: `htmlType="tel"`). 생략 시 `type`(프리셋/동작)에 맞게 매핑됩니다. */
  htmlType?: React.HTMLInputTypeAttribute;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoComplete?: string;
  placeholder?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  size?: FieldSize;
  name?: string;
  label?: string;
  required?: boolean;
  requiredMessage?: string;
  icon?: SvgIconInput;
  iconTheme?: SvgIconTheme;
  unit?: string;
  unitIcon?: SvgIconInput;
  suffix?: React.ReactNode;
  value?: string;
  defaultValue?: string;
  helperMessage?: string;
  errorMessage?: string;
  disabled?: boolean;
  view?: boolean;
  onChange?: (value: string) => void;
  validate?: (value: string) => string | null;
  className?: string;

  min?: number;
  max?: number;
  step?: number;
  comma?: boolean;
}

// ── Component ────────────────────────────────────
export default function Input({
  type: typeOrPresetProp,
  htmlType,
  inputMode: inputModeProp,
  autoComplete,
  placeholder,
  onKeyDown: onKeyDownProp,
  onBlur: onBlurProp,
  size: sizeProp,
  name: nameProp,
  label: labelProp,
  required: requiredProp,
  requiredMessage,
  icon: iconProp,
  iconTheme: iconThemeProp,
  unit,
  unitIcon,
  suffix: suffixProp,
  value: valueProp,
  defaultValue = '',
  helperMessage,
  errorMessage: externalError,
  disabled = false,
  view = false,
  onChange,
  validate,
  className,
  min,
  max,
  step = 1,
  comma: commaProp,
}: InputProps) {
  const presetKey: InputPreset | undefined =
    typeOrPresetProp !== undefined && isInputPreset(typeOrPresetProp) ? typeOrPresetProp : undefined;
  const preset = presetKey ? VARIANT_PRESETS[presetKey] : undefined;

  const type: InputType =
    presetKey !== undefined ? VARIANT_PRESETS[presetKey].inputType : (typeOrPresetProp as InputType | undefined) ?? 'text';

  const label = labelProp ?? preset?.label;
  const required = requiredProp ?? preset?.required;
  const icon = iconProp ?? preset?.icon;
  const iconTheme = iconThemeProp ?? preset?.iconTheme;
  const name = nameProp ?? preset?.name;
  const comma = commaProp ?? preset?.comma ?? false;
  const isUserCountField = presetKey === 'userCount' || name === 'userCount';

  const effectiveValidate =
    validate ?? (presetKey ? VARIANT_VALIDATORS[presetKey] : undefined) ?? DEFAULT_VALIDATORS[type];

  const size = sizeProp ?? 'lg';
  const inputId = useId();
  const isControlled = valueProp !== undefined;

  const [internalValue, setInternalValue] = useState(() => {
    if (type === 'date') return maskDate(defaultValue);
    if (type === 'yearMonth') return maskYearMonth(defaultValue);
    return defaultValue;
  });
  const [isFocused, setIsFocused] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);

  const rawValue = isControlled ? valueProp! : internalValue;
  const errorMessage = externalError ?? internalError;

  const effectiveMin = useMemo(
    () => (type === 'tel' ? min : min ?? (type === 'number' ? 1 : undefined)),
    [type, min],
  );
  const effectiveMax = max ?? preset?.max;

  const composedValidate = useMemo(() => {
    const hasRange = type === 'number' && (effectiveMin !== undefined || effectiveMax !== undefined);
    if (!effectiveValidate && !hasRange) return undefined;
    return (value: string): string | null => {
      if (value.trim() === '') return null;
      const baseError = effectiveValidate?.(value, label);
      if (baseError) return baseError;
      if (hasRange) {
        const n = Number(value.replace(/,/g, ''));
        if (!Number.isNaN(n)) {
          if (effectiveMin !== undefined && n < effectiveMin)
            return `${label ?? ''}${eunNeun(label ?? '')} ${effectiveMin} 이상 입력해 주세요.`;
          if (effectiveMax !== undefined && n > effectiveMax)
            return `${label ?? ''}${eunNeun(label ?? '')} ${effectiveMax} 이하로 입력해 주세요.`;
        }
      }
      return null;
    };
  }, [effectiveValidate, type, effectiveMin, effectiveMax, label]);

  const displayValue = useMemo(() => {
    if (type === 'tel') return applyTel(rawValue);
    if (type === 'number' && comma) return applyComma(rawValue);
    return rawValue;
  }, [type, rawValue, comma]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let next = e.target.value;

      if (type === 'tel') {
        next = next.replace(/[^0-9]/g, '').slice(0, 11);
      } else if (type === 'number') {
        next = next.replace(/[^0-9]/g, '');
      } else if (type === 'date') {
        next = maskDate(next);
      } else if (type === 'yearMonth') {
        next = maskYearMonth(next);
      }

      if (!isControlled) setInternalValue(next);
      onChange?.(next);
      if (next.trim() !== '') {
        setInternalError(composedValidate ? composedValidate(next) : null);
      }
    },
    [type, isControlled, onChange, composedValidate],
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    setInternalError(null);
  }, []);

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlurProp?.(e);

      let valueForValidation = rawValue;
      if (isUserCountField && rawValue.trim() !== '') {
        const normalized = normalizeUserCountDigits(rawValue.replace(/,/g, ''));
        valueForValidation = normalized;
        if (normalized !== rawValue) {
          if (!isControlled) setInternalValue(normalized);
          onChange?.(normalized);
        }
      }

      if (required && valueForValidation.trim() === '') {
        setInternalError(
          requiredMessage ?? (label ? `${label}${eulReul(label)} 입력해 주세요.` : '필수 항목입니다.'),
        );
        return;
      }

      if (composedValidate) setInternalError(composedValidate(valueForValidation));
    },
    [composedValidate, rawValue, required, requiredMessage, label, onBlurProp, isUserCountField, isControlled, onChange],
  );

  const handleStep = useCallback(
    (direction: 1 | -1) => {
      const current = parseDigitsInt(rawValue);
      let next = current + direction * step;
      if (effectiveMin !== undefined) next = Math.max(effectiveMin, next);
      if (effectiveMax !== undefined) next = Math.min(effectiveMax, next);
      const nextStr = String(next);
      if (!isControlled) setInternalValue(nextStr);
      setInternalError(null);
      onChange?.(nextStr);
    },
    [rawValue, step, effectiveMin, effectiveMax, isControlled, onChange],
  );

  const resolvedHtmlType = htmlType ?? (htmlTypeMap[type] ?? 'text');
  const resolvedInputMode = inputModeProp ?? inputModeMap[type];
  const resolvedAutoComplete = autoComplete ?? autoCompleteMap[type];
  const resolvedPlaceholder =
    placeholder ?? (type === 'date' ? 'YYYY-MM-DD' : type === 'yearMonth' ? 'YYYY-MM' : undefined);

  const hasValue = displayValue.length > 0;
  const atOrBelowMin =
    type === 'number' && effectiveMin !== undefined && parseDigitsInt(rawValue) <= effectiveMin;

  const suffix: React.ReactNode = useMemo(() => {
    if (suffixProp !== undefined) return suffixProp;

    if (type === 'number') {
      return (
        <span className={styles.spinner}>
          <button
            type="button"
            tabIndex={-1}
            disabled={view || disabled}
            onClick={() => handleStep(1)}
            aria-label="증가"
          >
            <Icon name={ArrowTriangle} direction="up" size={20} />
          </button>
          <button
            type="button"
            tabIndex={-1}
            disabled={view || disabled || atOrBelowMin}
            onClick={() => handleStep(-1)}
            aria-label="감소"
          >
            <Icon name={ArrowTriangle} direction="down" size={20} />
          </button>
        </span>
      );
    }

    return null;
  }, [suffixProp, type, atOrBelowMin, handleStep, view, disabled]);

  const isLabelVisible = label && !isFocused && !hasValue;

  const focusHelperMessage =
    isFocused && !hasValue
      ? (helperMessage ?? requiredMessage ?? (label ? `${label}${eulReul(label)} 입력해 주세요.` : undefined))
      : undefined;
  const resolvedHelperMessage =
    helperMessage && !requiredMessage
      ? helperMessage
      : (preset?.helperMessage ?? focusHelperMessage);

  const containerCls = clsx(styles.field, styles[`size_${size}`], isFocused && !errorMessage && styles.focused, errorMessage && styles.error, !isFocused && hasValue && !errorMessage && !disabled && !view && styles.completed, disabled && styles.disabled, view && styles.view, className);

  const iconSlotCls = clsx(styles.icon_slot, isFocused && !errorMessage && styles.icon_focused, errorMessage && styles.icon_error, disabled && styles.icon_disabled, view && styles.icon_view);

  const inputBlock = (
    <div className={styles.wrapper}>
      <div className={containerCls}>
        {icon && (
          <span className={iconSlotCls}>
            <Icon name={icon} theme={iconTheme} size={iconSizeMap[size]} />
          </span>
        )}

        <div className={styles.input_area}>
          {label && (
            <label
              htmlFor={inputId}
              className={clsx(styles.label, !isLabelVisible && styles.label_hidden)}
              aria-hidden={!isLabelVisible}
            >
              {label}
              {required && <span className={styles.required}>*</span>}
            </label>
          )}

          <input
            id={inputId}
            className={styles.input}
            type={resolvedHtmlType}
            name={name ?? undefined}
            inputMode={resolvedInputMode}
            placeholder={resolvedPlaceholder}
            value={displayValue}
            disabled={disabled}
            readOnly={view}
            onChange={view ? undefined : handleChange}
            onFocus={view ? undefined : handleFocus}
            onBlur={view ? undefined : handleBlur}
            onKeyDown={view ? undefined : onKeyDownProp}
            autoComplete={resolvedAutoComplete}
          />
        </div>

        {(unit || unitIcon) && (
          <span className={styles.unit}>
            {unit}
            {unitIcon && (
              <Icon name={unitIcon} size={iconSizeMap[size]} />
            )}
          </span>
        )}

        {suffix && <span className={styles.suffix}>{suffix}</span>}
      </div>

      <FormMessage errorMessage={errorMessage ?? undefined} helperMessage={resolvedHelperMessage} />
    </div>
  );

  return inputBlock;
}
